import { Box, BoxProps } from "@mui/material";
import { useEffect, useState, Fragment, useContext } from "react";
import BottomButtons from "../../components/bottomButtons";
import NameRow from "../../components/nameRow";
import ScoreButtonGroup from "../../components/scoreButtonGroup";
import { CRICKET_NUMBERS } from "../../types/dart";
import { PLAYER } from "../../types/players";
import { GAME_STATE } from "../../types/game";
import { CricketGameState } from "../../types/cricket";
import {
  getNextCricketState,
  numberOrder,
  getInitCricketState,
  doBotMove,
  getBotTurnMarks,
  endGame,
  findNextNumber,
  getBotTurnBullMarks,
} from "../../util/cricketLogic";
import { cloneDeep } from "lodash";
import CricketMark from "../../components/cricketMark";
import { styled } from "@mui/material/styles";
import EndGameModal from "../../components/endGameModal";
import { BaseMatchState, GAME_TYPE, MATCH_TYPE } from "../../types/match";
import ResultScreen from "../results/results";
import colors from "../../theme/colors";
import fonts from "../../theme/fonts";
import { OnlineMatchContext } from "../../contexts/OnlineMatchContext";
import UndoModal from "../../components/undoModal";
import { continueUndoHandling, getNextThrower } from "../../util/matchLogic";

interface CricketProps {
  matchState: BaseMatchState;
  endLeg: Function;
  rematch: Function;
  restart: boolean;
  setRestart: Function;
}

export default function Cricket({
  matchState,
  endLeg,
  rematch,
  restart,
  setRestart,
}: CricketProps) {
  const {
    online,
    onlinePlayer,
    onlineGameState,
    handleCricketHit,
    handleEndLeg,
    handleUndoRequest,
    handleDenyUndo,
  } = useContext(OnlineMatchContext);
  const [gameState, setGameState] = useState<CricketGameState>(
    getInitCricketState(matchState.matchType)
  );
  const [oldStates, setOldStates] = useState<CricketGameState[]>([]);
  const [inUndoState, setInUndoState] = useState<boolean>(false);
  const [botThinking, setBotThinking] = useState<boolean>(false);
  const [buttonHeight, setButtonHeight] = useState(0);
  const [botTurnMarks, setBotTurnMarks] = useState(0);
  const [undoCanceled, setUndoCanceled] = useState(false);

  matchState.gameType = GAME_TYPE.CRICKET;
  const botTurn =
    matchState.matchType === MATCH_TYPE.BOT &&
    gameState.currentPlayer === PLAYER.SECOND;
  const disableButton =
    botTurn || (online && gameState?.currentPlayer !== onlinePlayer);

  useEffect(() => {
    if (!onlineGameState || onlineGameState === gameState) return;
    setGameState(onlineGameState as CricketGameState);
    // eslint-disable-next-line
  }, [onlineGameState]);

  useEffect(() => {
    if (restart) {
      rematchHandler();
    }
    // eslint-disable-next-line
  }, [restart]);

  useEffect(() => {
    if (
      gameState.gameState === GAME_STATE.PENDING ||
      gameState.gameState === GAME_STATE.FINISHED ||
      inUndoState
    )
      return;
    if (
      matchState.matchType === MATCH_TYPE.BOT &&
      gameState.currentPlayer === PLAYER.SECOND
    ) {
      if (gameState.throwNumber === 1) {
        const nextNumber = findNextNumber(gameState);
        const marks =
          nextNumber === CRICKET_NUMBERS.BULL
            ? getBotTurnBullMarks(matchState.botDifficulty)
            : getBotTurnMarks(matchState.botDifficulty);
        setBotTurnMarks(marks);
        botTimeout(marks);
      } else if (gameState.throwNumber > 1) {
        botTimeout(botTurnMarks);
      }
    }
    // eslint-disable-next-line
  }, [gameState, inUndoState]);

  const botTimeout = (botTurnMarks: number) =>
    setTimeout(() => {
      if (botThinking || inUndoState) return;
      setBotThinking(true);
      const oldGameState = cloneDeep(gameState);
      setOldStates([...oldStates, oldGameState]);
      const botMove = doBotMove(
        oldGameState,
        botTurnMarks,
        matchState.botDifficulty
      );
      setGameState(botMove);
      setBotThinking(false);
    }, 1250);

  const onClickHandler = async (number: number, multiplier: number) => {
    if (
      !gameState ||
      gameState.gameState === GAME_STATE.PENDING ||
      gameState.gameState === GAME_STATE.FINISHED
    )
      return;
    let newState: CricketGameState;
    const oldGameState = cloneDeep(gameState);
    setOldStates([...oldStates, oldGameState]);
    if (!online) {
      newState = getNextCricketState(number, multiplier, gameState);
    } else {
      newState = await handleCricketHit(number, multiplier);
      if (!newState) return;
    }
    setOldStates([...oldStates, oldGameState]);
    setGameState(newState);
    setInUndoState(false);
    setUndoCanceled(false);
  };

  const undoHandler = async () => {
    if (
      !continueUndoHandling(
        oldStates,
        undoCanceled,
        online,
        onlinePlayer,
        gameState,
        matchState.matchType
      )
    )
      return;

    setInUndoState(true);
    const states = [...oldStates];
    let latestOldState: CricketGameState = states[states.length - 1];
    const updatedOldStates: CricketGameState[] = states.slice(0, -1);

    if (online) {
      const undoState = await handleUndoRequest();
      if (!Boolean(undoState?.error)) latestOldState = undoState;
    }

    setOldStates(updatedOldStates);
    setGameState(latestOldState);
    setUndoCanceled(false);
  };

  const endGameHandler = async () => {
    let newState: CricketGameState;
    if (!online) {
      newState = endGame(gameState);
      endLeg(newState);
    } else {
      newState = await handleEndLeg();
    }

    setOldStates([]);
    setGameState(newState);
  };

  const newGameHandler = () => {
    const newGameState = getInitCricketState(matchState.matchType);
    newGameState.currentPlayer = getNextThrower(gameState);
    newGameState.gameState = GAME_STATE.ACTIVE;
    setGameState(newGameState);
  };

  const rematchHandler = () => {
    rematch();
    setGameState(getInitCricketState(matchState.matchType));
    setRestart(false);
  };

  return (
    <Wrapper
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      alignItems="center"
      gap="4px"
    >
      {gameState.gameState !== GAME_STATE.FINISHED && (
        <Fragment>
          <Box flex={1} width="100%" display="flex" justifyContent="center">
            <NameRow
              team1Score={gameState.team1.score}
              team2Score={gameState.team2.score}
              throwNumber={gameState.throwNumber}
              activeThrower={gameState.currentPlayer}
            />
          </Box>
          <MiddleWrapper>
            <ScoreMarkWrapper player={PLAYER.FIRST}>
              <ScoreColumn>
                <Score>Score:</Score>
                <Box>{gameState.team1.score}</Box>
              </ScoreColumn>
              <MarkColumn>
                {numberOrder.map((number) => {
                  return (
                    <CricketMark
                      key={`p1_${number}`}
                      height={buttonHeight}
                      markAmount={gameState.team1.numbers[number]}
                    />
                  );
                })}
              </MarkColumn>
            </ScoreMarkWrapper>
            <ButtonColumn>
              {numberOrder.map((number) => {
                return (
                  <ScoreButtonGroup
                    key={number}
                    number={number}
                    player={gameState.currentPlayer}
                    disabled={disableButton}
                    multiples={number === CRICKET_NUMBERS.BULL ? 2 : 3}
                    onClickHandler={onClickHandler}
                    setButtonHeight={setButtonHeight}
                  />
                );
              })}
            </ButtonColumn>
            <ScoreMarkWrapper player={PLAYER.SECOND}>
              <MarkColumn>
                {numberOrder.map((number) => {
                  return (
                    <CricketMark
                      key={`p2_${number}`}
                      height={buttonHeight}
                      markAmount={gameState.team2.numbers[number]}
                    />
                  );
                })}
              </MarkColumn>
              <ScoreColumn>
                <Score>Score:</Score>
                <Box>{gameState.team2.score}</Box>
              </ScoreColumn>
            </ScoreMarkWrapper>
          </MiddleWrapper>
          <Box
            flex={1}
            width="100%"
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <BottomButtons
              botTurn={disableButton}
              inUndoState={inUndoState}
              undoHandler={undoHandler}
              onClickHandler={onClickHandler}
            />
          </Box>
        </Fragment>
      )}
      {gameState.gameState === GAME_STATE.FINISHED && (
        <ResultScreen
          newGameHandler={newGameHandler}
          matchState={matchState}
          rematchHandler={rematchHandler}
        />
      )}
      <EndGameModal
        open={
          gameState.gameState === GAME_STATE.PENDING &&
          (online ? onlinePlayer === gameState.currentPlayer : true)
        }
        undo={undoHandler}
        endGame={endGameHandler}
      />
      <UndoModal
        open={
          Boolean(gameState.requests?.undo) &&
          !undoCanceled &&
          gameState.requests?.undo !== onlinePlayer
        }
        undo={undoHandler}
        cancel={async () => {
          await handleDenyUndo();
          setUndoCanceled(true);
        }}
        requestedPlayer={gameState.requests?.undo}
      />
    </Wrapper>
  );
}

const Wrapper = styled(Box)({
  height: "100%",
  width: "100%",
});

const MiddleWrapper = styled(Box)<BoxProps>(() => ({
  flex: 7,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  borderTop: "4px double black",
}));

interface ScoreMarkWrapperProps extends BoxProps {
  player: PLAYER;
}

const ScoreMarkWrapper = styled(Box)<ScoreMarkWrapperProps>(({ player }) => ({
  flex: 1,
  height: "100%",
  display: "flex",
  justifyContent: player === PLAYER.FIRST ? "end" : "start",
  "@media (max-width: 650px)": {
    justifyContent: "center",
    width: "100%",
  },
}));

const MarkColumn = styled(Box)<BoxProps>(() => ({
  height: "100%",
  width: "50%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-evenly",
}));

const ScoreColumn = styled(Box)<BoxProps>(() => ({
  display: "flex",
  fontFamily: fonts.family.chalk,
  flexDirection: "column",
  fontSize: fonts.size.extraLarge,
  marginTop: "15%",
  padding: "5px",
  color: colors.lightBeige,
  "@media (max-width: 1000px)": {
    // fontSize: getFontSizeString(2.3),
    // fontSize: getFontSizeString(24),
    marginTop: "25%",
  },
  "@media (max-width: 650px)": {
    display: "none",
  },
}));

const Score = styled(Box)({
  fontSize: fonts.size.large,
});

const ButtonColumn = styled(Box)<BoxProps>(() => ({
  height: "100%",
  flex: 1.5,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-around",
  gap: 2,
  borderLeft: "4px double black",
  borderRight: "4px double black",
  paddingLeft: 20,
  paddingRight: 20,
  paddingTop: "4px",
  "@media (max-width: 650px)": {
    paddingLeft: 2,
    paddingRight: 2,
  },
}));
